import {
  ArrowLeft,
  ArrowRight,
  CircleCheck,
  Clock,
  Compass,
  Info,
  KeyRound,
  Lightbulb,
  Receipt,
  ShieldAlert,
  TriangleAlert,
  Users,
  Webhook as WebhookIcon,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { useOps } from '../../../hooks/useOps';
import { cn } from '../../../lib/utils';
import config from '../../../utils/config';
import { BlocoCodigo } from '../components/copiar';
import {
  Endpoint,
  Perfil,
  SECOES,
  SecaoId,
  endpoints,
  erros,
  errosToken,
  exemploToken,
  exemploWebhook,
  headersWebhook,
  perfilDaOps,
  verificacaoNode,
  verificacaoPython,
} from './api-docs';

// Sem REACT_APP_API_FOR_CLIENT_URL (domínio ainda não configurado), mostra o de prod.
const URL_PADRAO = 'https://api-clientes.planaltocapital.com.br';

const json = (valor: unknown) => JSON.stringify(valor, null, 2);

const ICONES: Record<SecaoId, LucideIcon> = {
  'visao-geral': Compass,
  autenticacao: KeyRound,
  antecipacoes: Zap,
  entregadores: Users,
  financeiro: Receipt,
  webhook: WebhookIcon,
  erros: TriangleAlert,
};

// Paleta: azul-marinho da marca (--primary) com o dourado (--accent) nos destaques.
const NAVY = 'text-[hsl(210,50%,10%)]';
const MUTED = 'text-[hsl(215,16%,47%)]';
const BORDA = 'border-[hsl(214,32%,91%)]';

// `code` inline (fora dos blocos de código)
const CODE_INLINE =
  '[&_:not(pre)>code]:rounded [&_:not(pre)>code]:border [&_:not(pre)>code]:border-[hsl(214,32%,88%)] [&_:not(pre)>code]:bg-[hsl(210,40%,97%)] [&_:not(pre)>code]:px-1.5 [&_:not(pre)>code]:py-0.5 [&_:not(pre)>code]:font-mono [&_:not(pre)>code]:text-[0.85em] [&_:not(pre)>code]:text-[hsl(210,35%,32%)]';

// Documentação da ApiForClient: menu lateral e uma seção por vez (?secao=).
export function ApiDocs() {
  const { currentOps } = useOps();
  const [params, setParams] = useSearchParams();
  const perfil = perfilDaOps(currentOps);
  const baseUrl = config.apiForClient || URL_PADRAO;

  const atual: SecaoId = SECOES.some((s) => s.id === params.get('secao'))
    ? (params.get('secao') as SecaoId)
    : 'visao-geral';
  const indice = SECOES.findIndex((s) => s.id === atual);
  const anterior = SECOES[indice - 1];
  const proxima = SECOES[indice + 1];

  const irPara = (secao: SecaoId) => {
    const novos = new URLSearchParams(params);
    novos.set('secao', secao);
    setParams(novos);
  };

  const endpoint = endpoints(perfil).find((e) => e.id === atual);

  return (
    <div className="grid gap-6 lg:grid-cols-[230px_1fr] lg:gap-10">
      <div className="lg:hidden">
        <Select value={atual} onValueChange={(v) => irPara(v as SecaoId)}>
          <SelectTrigger aria-label="Seção da documentação">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SECOES.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.titulo}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <nav aria-label="Seções da documentação" className="hidden lg:block">
        <div className="sticky top-4 space-y-3">
          <div className="flex items-center gap-2 px-3">
            <span className={cn('text-xs font-semibold uppercase tracking-wider', MUTED)}>API</span>
            <span className="rounded-full bg-[hsl(43,56%,61%)]/20 px-2 py-0.5 text-[10px] font-semibold text-[hsl(38,60%,32%)]">
              v1
            </span>
          </div>
          <ul className="space-y-0.5 text-sm">
            {SECOES.map((s) => {
              const Icone = ICONES[s.id];
              const ativa = s.id === atual;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => irPara(s.id)}
                    aria-current={ativa ? 'page' : undefined}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-md border-l-[3px] px-3 py-2 text-left transition-colors',
                      ativa
                        ? 'border-[hsl(43,56%,61%)] bg-[hsl(210,35%,32%)]/[0.08] font-medium text-[hsl(210,35%,25%)]'
                        : 'border-transparent text-[hsl(215,16%,40%)] hover:bg-[hsl(210,40%,96%)] hover:text-[hsl(210,50%,10%)]'
                    )}
                  >
                    <Icone
                      className={cn('h-4 w-4 shrink-0', ativa ? 'text-[hsl(210,35%,32%)]' : 'text-[hsl(215,16%,60%)]')}
                    />
                    {s.titulo}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>

      <div className="min-w-0 space-y-8">
        {atual === 'visao-geral' && <VisaoGeral perfil={perfil} baseUrl={baseUrl} irPara={irPara} />}
        {atual === 'autenticacao' && <Autenticacao baseUrl={baseUrl} />}
        {endpoint && <SecaoEndpoint endpoint={endpoint} baseUrl={baseUrl} />}
        {atual === 'webhook' && <Webhook perfil={perfil} />}
        {atual === 'erros' && <Erros />}

        <div className={cn('grid gap-3 border-t pt-6 sm:grid-cols-2', BORDA)}>
          {anterior ? (
            <BotaoNavegacao direcao="anterior" titulo={anterior.titulo} onClick={() => irPara(anterior.id)} />
          ) : (
            <span className="hidden sm:block" />
          )}
          {proxima && (
            <BotaoNavegacao direcao="proxima" titulo={proxima.titulo} onClick={() => irPara(proxima.id)} />
          )}
        </div>
      </div>
    </div>
  );
}

// O nome acessível do botão é só o título da seção ("Anterior"/"Próxima" é decorativo).
function BotaoNavegacao({
  direcao,
  titulo,
  onClick,
}: {
  direcao: 'anterior' | 'proxima';
  titulo: string;
  onClick: () => void;
}) {
  const proxima = direcao === 'proxima';
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex items-center gap-3 rounded-lg border px-4 py-3 transition-colors hover:border-[hsl(43,56%,61%)] hover:bg-[hsl(43,56%,61%)]/[0.06]',
        BORDA,
        proxima ? 'justify-end text-right sm:col-start-2' : 'text-left'
      )}
    >
      {!proxima && <ArrowLeft className="h-4 w-4 text-[hsl(215,16%,60%)] group-hover:text-[hsl(38,60%,40%)]" />}
      <span className="flex flex-col">
        <span aria-hidden="true" className={cn('text-xs', MUTED)}>
          {proxima ? 'Próxima' : 'Anterior'}
        </span>
        <span className={cn('text-sm font-medium', NAVY)}>{titulo}</span>
      </span>
      {proxima && <ArrowRight className="h-4 w-4 text-[hsl(215,16%,60%)] group-hover:text-[hsl(38,60%,40%)]" />}
    </button>
  );
}

function Secao({
  id,
  titulo,
  resumo,
  children,
}: {
  id: SecaoId;
  titulo: string;
  resumo?: ReactNode;
  children: ReactNode;
}) {
  const Icone = ICONES[id];
  return (
    <section className={cn('space-y-5 text-sm leading-relaxed text-[hsl(215,16%,30%)]', CODE_INLINE)}>
      <header className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[hsl(210,35%,32%)] text-white shadow-sm">
          <Icone className="h-5 w-5" />
        </span>
        <div className="space-y-1">
          <h2 className={cn('text-xl font-semibold', NAVY)}>{titulo}</h2>
          {resumo && <p className={MUTED}>{resumo}</p>}
        </div>
      </header>
      {children}
    </section>
  );
}

function Subtitulo({ children }: { children: ReactNode }) {
  return (
    <h3 className={cn('flex items-center gap-2 pt-2 text-base font-semibold', NAVY)}>
      <span className="h-4 w-1 rounded-full bg-[hsl(43,56%,61%)]" />
      {children}
    </h3>
  );
}

function Rota({ caminho }: { caminho: string }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border bg-[hsl(210,40%,98%)] px-3 py-2 font-mono text-sm',
        BORDA
      )}
    >
      <span className="rounded bg-[hsl(152,60%,92%)] px-2 py-0.5 text-xs font-bold tracking-wide text-[hsl(152,65%,26%)]">
        POST
      </span>
      <span className={cn('break-all font-medium', NAVY)}>{caminho}</span>
    </div>
  );
}

const ESTILO_AVISO = {
  info: { icone: Info, caixa: 'border-[hsl(210,80%,85%)] bg-[hsl(210,80%,97%)]', cor: 'text-[hsl(210,80%,40%)]' },
  dica: { icone: Lightbulb, caixa: 'border-[hsl(43,56%,75%)] bg-[hsl(43,56%,61%)]/[0.08]', cor: 'text-[hsl(38,60%,38%)]' },
  alerta: { icone: ShieldAlert, caixa: 'border-[hsl(0,70%,88%)] bg-[hsl(0,70%,97%)]', cor: 'text-[hsl(0,65%,45%)]' },
};

function Aviso({
  tipo,
  titulo,
  children,
}: {
  tipo: keyof typeof ESTILO_AVISO;
  titulo?: string;
  children: ReactNode;
}) {
  const { icone: Icone, caixa, cor } = ESTILO_AVISO[tipo];
  return (
    <div className={cn('flex gap-3 rounded-lg border px-4 py-3', caixa)}>
      <Icone className={cn('mt-0.5 h-4 w-4 shrink-0', cor)} />
      <div className="min-w-0 space-y-1">
        {titulo && <p className={cn('font-semibold', cor)}>{titulo}</p>}
        <div className="space-y-1">{children}</div>
      </div>
    </div>
  );
}

function ListaCheck({ itens }: { itens: ReactNode[] }) {
  return (
    <ul className="space-y-2">
      {itens.map((item, i) => (
        <li key={i} className="flex gap-2.5">
          <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(152,60%,38%)]" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function Tabela({ children }: { children: ReactNode }) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border [&_thead_tr]:bg-[hsl(210,40%,97%)] [&_th]:font-semibold [&_th]:text-[hsl(210,35%,32%)]',
        BORDA
      )}
    >
      <Table>{children}</Table>
    </div>
  );
}

function StatusHttp({ status }: { status: number }) {
  const cor =
    status < 300
      ? 'bg-[hsl(152,60%,92%)] text-[hsl(152,65%,26%)]'
      : status < 500
        ? 'bg-[hsl(38,90%,92%)] text-[hsl(30,80%,32%)]'
        : 'bg-[hsl(0,80%,94%)] text-[hsl(0,65%,40%)]';
  return <span className={cn('rounded px-2 py-0.5 font-mono text-xs font-bold', cor)}>{status}</span>;
}

function NomeCampo({ children }: { children: ReactNode }) {
  return <span className="font-mono text-[13px] font-medium text-[hsl(210,35%,32%)]">{children}</span>;
}

function VisaoGeral({
  perfil,
  baseUrl,
  irPara,
}: {
  perfil: Perfil;
  baseUrl: string;
  irPara: (secao: SecaoId) => void;
}) {
  const link = (secao: SecaoId, texto: string) => (
    <button
      type="button"
      className="font-medium text-[hsl(210,80%,40%)] underline decoration-[hsl(210,80%,40%)]/30 underline-offset-2 hover:decoration-[hsl(210,80%,40%)]"
      onClick={() => irPara(secao)}
    >
      {texto}
    </button>
  );

  const passos: ReactNode[] = [
    <>
      Crie uma credencial na aba <strong>Credenciais</strong> e guarde o <code>client_secret</code>:
      ele aparece uma única vez.
    </>,
    <>Troque a credencial por um token de acesso ({link('autenticacao', 'Autenticação')}).</>,
    <>
      Chame os endpoints com o token: {link('antecipacoes', 'antecipações')},{' '}
      {link('entregadores', 'entregadores')} e {link('financeiro', 'financeiro')}.
    </>,
    <>
      Opcional: cadastre um {link('webhook', 'webhook')} para receber as antecipações novas sem
      precisar consultar.
    </>,
  ];

  return (
    <Secao
      id="visao-geral"
      titulo="Visão geral"
      resumo={
        <>
          A API da Planalto dá ao sistema da sua operação acesso às antecipações dos entregadores,
          ao cadastro e aos bloqueios, e ao fechamento financeiro
          {perfil === 'franquia' && ' com as notas fiscais'}.
        </>
      }
    >
      <BlocoCodigo titulo="URL base" codigo={baseUrl} />

      <Subtitulo>Como começar</Subtitulo>
      <ol className="space-y-3">
        {passos.map((p, i) => (
          <li key={i} className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(43,56%,61%)] text-xs font-bold text-[hsl(210,50%,10%)]">
              {i + 1}
            </span>
            <span className="pt-0.5">{p}</span>
          </li>
        ))}
      </ol>

      <Subtitulo>Convenções</Subtitulo>
      <ListaCheck
        itens={[
          <>
            Todas as chamadas são <code>POST</code> com corpo JSON (
            <code>Content-Type: application/json</code>).
          </>,
          <>
            A credencial identifica a sua operação: não é preciso informá-la nas chamadas, e as
            respostas trazem só os dados dela.
          </>,
          <>
            Valores monetários são inteiros em <strong>centavos</strong> (<code>15000</code> = R$
            150,00).
          </>,
          <>
            Datas no formato <code>YYYY-MM-DD</code>; dias e horários no horário de Brasília
            (America/Sao_Paulo).
          </>,
        ]}
      />
    </Secao>
  );
}

function Autenticacao({ baseUrl }: { baseUrl: string }) {
  const curlToken = [
    `curl -X POST "${baseUrl}/v1/token" \\`,
    `  -u "$CLIENT_ID:$CLIENT_SECRET" \\`,
    `  -d "grant_type=client_credentials"`,
  ].join('\n');

  return (
    <Secao
      id="autenticacao"
      titulo="Autenticação"
      resumo={
        <>
          OAuth 2.0 <strong>client credentials</strong>: troque o <code>client_id</code> e o{' '}
          <code>client_secret</code> por um token e envie o token em todas as chamadas.
        </>
      }
    >
      <Subtitulo>1. Pedir o token</Subtitulo>
      <Rota caminho="/v1/token" />
      <p>
        Envie <code>grant_type=client_credentials</code> como formulário (
        <code>application/x-www-form-urlencoded</code>) e a credencial em HTTP Basic. Também são
        aceitos <code>client_id</code> e <code>client_secret</code> no próprio formulário ou em um
        corpo JSON.
      </p>
      <BlocoCodigo titulo="Exemplo de requisição" codigo={curlToken} linguagem="bash" />
      <BlocoCodigo titulo="Resposta (200)" codigo={json(exemploToken)} linguagem="json" />

      <Subtitulo>2. Usar o token</Subtitulo>
      <BlocoCodigo titulo="Cabeçalho" codigo="Authorization: Bearer <access_token>" linguagem="http" />
      <Aviso tipo="info" titulo="Validade">
        <p>
          O token vale <strong>30 minutos</strong> (<code>expires_in</code>, em segundos).
          Reaproveite-o nas chamadas e peça outro quando expirar ou quando a API responder{' '}
          <code>401</code>.
        </p>
        <p>
          Gerar um novo secret ou revogar a credencial na aba Credenciais invalida na hora os tokens
          já emitidos.
        </p>
      </Aviso>
      <Aviso tipo="alerta" titulo="Proteja o client_secret">
        <p>
          Guarde o <code>client_secret</code> só no servidor. Nunca o coloque em aplicativo, site ou
          repositório de código.
        </p>
      </Aviso>

      <Subtitulo>Erros do token</Subtitulo>
      <p>
        Corpo <code>{'{ "error": "…", "message": "…" }'}</code>:
      </p>
      <Tabela>
        <TableHeader>
          <TableRow>
            <TableHead className="w-20">Status</TableHead>
            <TableHead>error</TableHead>
            <TableHead>Quando</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {errosToken.map((e) => (
            <TableRow key={e.error}>
              <TableCell>
                <StatusHttp status={e.status} />
              </TableCell>
              <TableCell>
                <NomeCampo>{e.error}</NomeCampo>
              </TableCell>
              <TableCell>{e.quando}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Tabela>
    </Secao>
  );
}

function SecaoEndpoint({ endpoint, baseUrl }: { endpoint: Endpoint; baseUrl: string }) {
  const curl = [
    `curl -X ${endpoint.metodo} "${baseUrl}${endpoint.caminho}" \\`,
    `  -H "Authorization: Bearer $TOKEN" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '${JSON.stringify(endpoint.exemploBody)}'`,
  ].join('\n');

  return (
    <Secao id={endpoint.id} titulo={endpoint.titulo} resumo={endpoint.descricao}>
      <Rota caminho={endpoint.caminho} />

      <Subtitulo>Corpo da requisição</Subtitulo>
      <Tabela>
        <TableHeader>
          <TableRow>
            <TableHead>Campo</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Obrigatório</TableHead>
            <TableHead>Descrição</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {endpoint.body.map((c) => (
            <TableRow key={c.nome}>
              <TableCell>
                <NomeCampo>{c.nome}</NomeCampo>
              </TableCell>
              <TableCell className="whitespace-nowrap font-mono text-xs text-[hsl(270,45%,45%)]">
                {c.tipo}
              </TableCell>
              <TableCell>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-medium',
                    c.obrigatorio
                      ? 'bg-[hsl(43,56%,61%)]/20 text-[hsl(38,60%,32%)]'
                      : 'bg-[hsl(210,40%,96%)] text-[hsl(215,16%,47%)]'
                  )}
                >
                  {c.obrigatorio ? 'sim' : 'não'}
                </span>
              </TableCell>
              <TableCell>{c.descricao}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Tabela>

      <Subtitulo>Exemplo</Subtitulo>
      <BlocoCodigo titulo="Exemplo de requisição" codigo={curl} linguagem="bash" />
      <BlocoCodigo titulo="Exemplo de resposta (200)" codigo={json(endpoint.exemploResposta)} linguagem="json" />

      {endpoint.observacoes && endpoint.observacoes.length > 0 && (
        <Aviso tipo="dica" titulo="Bom saber">
          <ul className="list-disc space-y-1 pl-4">
            {endpoint.observacoes.map((o) => (
              <li key={o}>{o}</li>
            ))}
          </ul>
        </Aviso>
      )}
    </Secao>
  );
}

function Webhook({ perfil }: { perfil: Perfil }) {
  return (
    <Secao
      id="webhook"
      titulo="Webhook de antecipações"
      resumo={
        <>
          Com um webhook cadastrado (aba <strong>Webhook</strong>), a Planalto envia um{' '}
          <code>POST</code> para a sua URL com as antecipações novas do dia, sem você precisar
          consultar o endpoint de antecipações.
        </>
      }
    >
      <Subtitulo>Quando é enviado</Subtitulo>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className={cn('flex gap-3 rounded-lg border bg-white px-4 py-3', BORDA)}>
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(210,35%,32%)]" />
          <span>A cada 2 minutos, das 06:00 às 17:30 (horário de Brasília).</span>
        </div>
        <div className={cn('flex gap-3 rounded-lg border bg-white px-4 py-3', BORDA)}>
          <Zap className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(38,60%,45%)]" />
          <span>Só quando há antecipação nova; cada antecipação vai uma vez, em lotes de até 500.</span>
        </div>
      </div>

      <Subtitulo>O que a sua URL deve fazer</Subtitulo>
      <ListaCheck
        itens={[
          <>
            Aceitar <code>POST</code> com <code>Content-Type: application/json</code> em uma URL{' '}
            <code>https://</code>.
          </>,
          <>
            Responder com qualquer status <code>2xx</code> em até <strong>10 segundos</strong>. O
            corpo da resposta é ignorado. Se precisar de processamento demorado, responda primeiro e
            processe depois.
          </>,
          <>
            Sem <code>2xx</code> no prazo (erro, timeout ou redirecionamento), o mesmo lote é
            reenviado na rodada seguinte, junto com as antecipações novas.
          </>,
          <>
            A entrega é "pelo menos uma vez": use o <code>id</code> de cada antecipação para
            descartar repetidas.
          </>,
        ]}
      />
      <Aviso tipo="info" titulo="Enviar teste">
        <p>
          O botão <strong>Enviar teste</strong> da aba Webhook manda um envio com{' '}
          <code>"evento": "teste"</code> e dado fictício: responda <code>2xx</code> e ignore o
          conteúdo.
        </p>
      </Aviso>

      <BlocoCodigo titulo="Corpo enviado" codigo={json(exemploWebhook(perfil))} linguagem="json" />

      <Subtitulo>Cabeçalhos</Subtitulo>
      <Tabela>
        <TableHeader>
          <TableRow>
            <TableHead>Cabeçalho</TableHead>
            <TableHead>Conteúdo</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {headersWebhook.map((h) => (
            <TableRow key={h.nome}>
              <TableCell className="whitespace-nowrap">
                <NomeCampo>{h.nome}</NomeCampo>
              </TableCell>
              <TableCell>{h.descricao}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Tabela>

      <Subtitulo>Conferir a assinatura (recomendado)</Subtitulo>
      <p>
        Qualquer um que descubra a sua URL pode mandar dados para ela. A assinatura prova que o
        envio veio da Planalto: calcule o HMAC-SHA256 de <code>{'<timestamp>.<corpo>'}</code> com o{' '}
        <strong>segredo</strong> da aba Webhook e compare com o cabeçalho. Recuse envios com o
        timestamp de mais de 5 minutos atrás.
      </p>
      <Aviso tipo="dica">
        <p>Conferir é opcional: sem isso o webhook funciona igual.</p>
      </Aviso>
      <BlocoCodigo titulo="Node.js" codigo={verificacaoNode} linguagem="js" />
      <BlocoCodigo titulo="Python" codigo={verificacaoPython} linguagem="python" />
    </Secao>
  );
}

function Erros() {
  return (
    <Secao
      id="erros"
      titulo="Erros"
      resumo={
        <>
          Os endpoints respondem erros com o status HTTP abaixo e o corpo{' '}
          <code>{'{ "message": "…" }'}</code>. Os erros do pedido de token estão em Autenticação.
        </>
      }
    >
      <Tabela>
        <TableHeader>
          <TableRow>
            <TableHead className="w-20">Status</TableHead>
            <TableHead>Quando</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {erros.map((e) => (
            <TableRow key={e.status}>
              <TableCell>
                <StatusHttp status={e.status} />
              </TableCell>
              <TableCell>{e.quando}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Tabela>
    </Secao>
  );
}
